import React, { useEffect, useState } from 'react';
import {
    Container, Box, Typography, Button, Grid, CircularProgress,
    Paper, Fade, Divider, Chip, useTheme
} from '@mui/material';
import { useParams, useNavigate } from 'react-router-dom';
import { getAuth } from 'firebase/auth';
import ArrowBackIcon from '@mui/icons-material/ArrowBack';
import QuizIcon from '@mui/icons-material/Quiz';
import CheckCircleOutlineIcon from '@mui/icons-material/CheckCircleOutline';
import CancelOutlinedIcon from '@mui/icons-material/CancelOutlined';
import AccessTimeIcon from '@mui/icons-material/AccessTime';
import PeopleOutlineIcon from '@mui/icons-material/PeopleOutline';
import HelpOutlineIcon from '@mui/icons-material/HelpOutline';
import InfoOutlinedIcon from '@mui/icons-material/InfoOutlined';
import { useLanguage } from './LanguageContext';

export default function ResultPage() {
    const { t } = useLanguage();
    const { quiz_id } = useParams();
    const navigate = useNavigate();
    const [quiz, setQuiz] = useState(null);
    const [userAnswers, setUserAnswers] = useState(null);
    const [loading, setLoading] = useState(true);
    const theme = useTheme();

    // Set page title
    useEffect(() => {
        document.title = `${t.quizResults} | ClassCapture`;
    }, [t]);

    // Fetch quiz details
    useEffect(() => {
        const fetchQuiz = async () => {
            try {
                const auth = getAuth();
                const token = await auth.currentUser.getIdToken();
                const url = `${process.env.REACT_APP_BACKEND_URL}/quizzes/${quiz_id}`;
                const response = await fetch(url, {
                    headers: { Authorization: `Bearer ${token}` },
                });
                const data = await response.json();
                if (response.ok) {
                    setQuiz(data);
                }
            } catch (err) {
                console.error('Error fetching quiz details:', err);
            } finally {
                setLoading(false);
            }
        };
        fetchQuiz();
    }, [quiz_id]);

    // Fetch user answers
    useEffect(() => {
        const fetchUserAnswers = async () => {
            try {
                const auth = getAuth();
                const token = await auth.currentUser.getIdToken();
                const url = `${process.env.REACT_APP_BACKEND_URL}/quizzes/${quiz_id}/answers`;
                const response = await fetch(url, {
                    headers: { Authorization: `Bearer ${token}` },
                });
                const data = await response.json();
                if (response.ok) {
                    setUserAnswers(data);
                } else {
                    console.error('Failed to fetch user answers:', data.msg);
                }
            } catch (err) {
                console.error('Error fetching user answers:', err);
            }
        };
        fetchUserAnswers();
    }, [quiz_id]);

    // Compute results
    const calculateResults = () => {
        const answers = userAnswers?.answers || [];
        const correct = answers.filter(a => a.isCorrect).length;
        const total = answers.length;
        const percentage = total ? Math.round((correct / total) * 100) : 0;
        return { correct, total, percentage };
    };
    const results = calculateResults();

    const gradientBackground = {
        background: `linear-gradient(135deg, ${theme.palette.primary.light} 0%, ${theme.palette.primary.main} 100%)`,
    };

    if (loading) {
        return (
            <Container sx={{
                display: 'flex', justifyContent: 'center', alignItems: 'center',
                height: '100vh', flexDirection: 'column', gap: 1
            }}>
                <QuizIcon sx={{ fontSize: 40, color: theme.palette.primary.main }} />
                <Typography variant="subtitle1" color="primary" fontWeight={450}>
                    ClassCapture
                </Typography>
                <CircularProgress color="primary" size={24} />
            </Container>
        );
    }

    if (!quiz) {
        return (
            <Container sx={{ py: 4 }}>
                <Fade in timeout={800}>
                    <Paper elevation={1} sx={{
                        p: 3, borderRadius: 1,
                        display: 'flex', flexDirection: 'column',
                        alignItems: 'center', gap: 2
                    }}>
                        <InfoOutlinedIcon color="error" sx={{ fontSize: 40 }} />
                        <Typography variant="h6" color="error">
                            {t.errorLoadingQuiz}
                        </Typography>
                        <Button
                            variant="contained"
                            startIcon={<ArrowBackIcon />}
                            onClick={() => navigate(-1)}
                        >
                            {t.goBack}
                        </Button>
                    </Paper>
                </Fade>
            </Container>
        );
    }

    return (
        <Container component="main" maxWidth="lg" sx={{ py: 2 }}>
            {/* Header */}
            <Fade in timeout={800}>
                <Paper elevation={1} sx={{
                    p: 2, mb: 2, borderRadius: 1,
                    display: 'flex', alignItems: 'center',
                    justifyContent: 'space-between',
                    ...gradientBackground, color: 'white'
                }}>
                    <Box sx={{ display: 'flex', alignItems: 'center' }}>
                        <QuizIcon sx={{ fontSize: 24, mr: 1, color:"black" }} />
                        <Typography variant="h6" component="h1" fontWeight={400} color={"black"}>
                            {t.quizResults}
                        </Typography>
                    </Box>
                    <Button
                        variant="outlined"
                        startIcon={<ArrowBackIcon />}
                        size="small"
                        onClick={() => navigate(-1)}
                        sx={{
                            color: 'white',
                            borderColor: 'rgba(255,255,255,0.5)',
                            '&:hover': {
                                borderColor: 'white',
                                backgroundColor: 'rgba(255,255,255,0.1)'
                            }
                        }}
                    >
                        {t.back}
                    </Button>
                </Paper>
            </Fade>

            {/* Summary */}
            <Fade in timeout={1000}>
                <Grid container spacing={2} sx={{ mb: 2 }}>
                    <Grid item xs={12} md={8}>
                        <Paper sx={{
                            p: 2, borderRadius: 1,
                            backgroundColor: 'background.paper',
                            height: '100%'
                        }}>
                            <Box sx={{ display: 'flex', alignItems: 'center', mb: 1 }}>
                                <HelpOutlineIcon sx={{ fontSize: 16, mr: 1, color: theme.palette.primary.main }} />
                                <Typography variant="subtitle1" fontWeight={450}>
                                    {t.quizInformation}
                                </Typography>
                            </Box>
                            <Typography variant="h5" gutterBottom sx={{ fontWeight: 500 }}>
                                {quiz.title}
                            </Typography>
                            <Grid container spacing={2}>
                                <Grid item xs={12} sm={6}>
                                    <Box sx={{ display: 'flex', alignItems: 'center', mb: 1 }}>
                                        <AccessTimeIcon sx={{ fontSize: 16, mr: 1, color: theme.palette.text.secondary }} />
                                        <Typography variant="body2" color="text.secondary">
                                            {t.timeLimit} <strong>{quiz.timeLimit} {t.minutes}</strong>
                                        </Typography>
                                    </Box>
                                    <Box sx={{ display: 'flex', alignItems: 'center' }}>
                                        <InfoOutlinedIcon sx={{ fontSize: 16, mr: 1, color: theme.palette.text.secondary }} />
                                        <Typography variant="body2" color="text.secondary">
                                            {t.quizID} <strong>{quiz.quiz_id}</strong>
                                        </Typography>
                                    </Box>
                                </Grid>
                                <Grid item xs={12} sm={6}>
                                    <Box sx={{ display: 'flex', alignItems: 'center', mb: 1 }}>
                                        <PeopleOutlineIcon sx={{ fontSize: 16, mr: 1, color: theme.palette.text.secondary }} />
                                        <Typography variant="body2" color="text.secondary">
                                            {t.participants} <strong>{quiz.numberOfParticipants}</strong>
                                        </Typography>
                                    </Box>
                                    <Box sx={{ display: 'flex', alignItems: 'center' }}>
                                        <InfoOutlinedIcon sx={{ fontSize: 16, mr: 1, color: theme.palette.text.secondary }} />
                                        <Typography variant="body2" color="text.secondary">
                                            {t.updated} <strong>{new Date(quiz.timestamps.updatedAt).toLocaleDateString()}</strong>
                                        </Typography>
                                    </Box>
                                </Grid>
                            </Grid>
                        </Paper>
                    </Grid>
                    <Grid item xs={12} md={4}>
                        <Paper sx={{
                            p: 2, borderRadius: 1,
                            backgroundColor: 'background.paper',
                            height: '100%', display: 'flex',
                            flexDirection: 'column'
                        }}>
                            <Box sx={{ display: 'flex', alignItems: 'center', mb: 1 }}>
                                <CheckCircleOutlineIcon sx={{ fontSize: 16, mr: 1, color: theme.palette.success.main }} />
                                <Typography variant="subtitle1" fontWeight={450}>
                                    {t.yourResults}
                                </Typography>
                            </Box>
                            <Box sx={{
                                display: 'flex', flexDirection: 'column',
                                alignItems: 'center', justifyContent: 'center',
                                flexGrow: 1, py: 2
                            }}>
                                <Typography variant="h3" sx={{
                                    fontWeight: 700,
                                    color: results.percentage >= 70
                                        ? theme.palette.success.main
                                        : theme.palette.error.main
                                }}>
                                    {results.percentage}%
                                </Typography>
                                <Typography variant="body2" color="text.secondary" sx={{ mt: 1 }}>
                                    {t.youAnswered} <strong>{results.correct}</strong> {t.outOf} <strong>{results.total}</strong> {t.questionsCorrectly}
                                </Typography>
                                <Chip
                                    label={results.percentage >= 70 ? t.passed : t.needsImprovement}
                                    color={results.percentage >= 70 ? 'success' : 'error'}
                                    variant="outlined"
                                    sx={{ mt: 2 }}
                                />
                            </Box>
                        </Paper>
                    </Grid>
                </Grid>
            </Fade>

            {/* Questions & Answers */}
            <Box sx={{ mb: 1, display: 'flex', alignItems: 'center' }}>
                <Typography variant="subtitle1" component="h2" fontWeight={450}>
                    {t.quizQuestionsAnswers}
                </Typography>
                <Divider sx={{ ml: 2, flexGrow: 1 }} />
            </Box>
            <Fade in timeout={1200}>
                <Grid container spacing={2}>
                    {quiz.questions.map((q, i) => {
                        const ans = userAnswers?.answers?.find(a => a.question_id === q.id);
                        const correctOption = q.correctAnswer;
                        return (
                            <Grid item xs={12} key={i}>
                                <Paper sx={{
                                    borderRadius: 1, overflow: 'hidden',
                                    '&:hover': { transform: 'translateY(-2px)', boxShadow: 2 }
                                }}>
                                    <Box sx={{
                                        p: 1, ...gradientBackground,
                                        color: 'white', display: 'flex',
                                        alignItems: 'center', justifyContent: 'space-between'
                                    }}>
                                        <Box sx={{ display: 'flex', alignItems: 'center' }}>
                                            <HelpOutlineIcon sx={{ mr: 1, fontSize: 16 }} />
                                            <Typography variant="body2" fontWeight={400}>
                                                {t.question} {i + 1}
                                            </Typography>
                                        </Box>
                                        {ans && (
                                            <Chip
                                                icon={ans.isCorrect
                                                    ? <CheckCircleOutlineIcon sx={{ fontSize: '16px', color: 'white' }} />
                                                    : <CancelOutlinedIcon sx={{ fontSize: '16px', color: 'white' }} />
                                                }
                                                label={ans.isCorrect ? t.correct : t.incorrect}
                                                color={ans.isCorrect ? 'success' : 'error'}
                                                size="small"
                                                sx={{ height: 20, '& .MuiChip-label': { px: 1, fontSize: '0.7rem' } }}
                                            />
                                        )}
                                    </Box>
                                    <Box sx={{ p: 2 }}>
                                        <Typography variant="body1" sx={{ fontWeight: 500, mb: 1.5 }}>
                                            {q.question}
                                        </Typography>
                                        <Divider sx={{ my: 1.5 }} />
                                        <Typography variant="body2" color="text.secondary" sx={{ mb: 1, fontWeight: 500 }}>
                                            {t.options}
                                        </Typography>
                                        <Grid container spacing={1}>
                                            {q.options.map((opt, idx) => {
                                                const isSelected = ans?.selectedOption === opt;
                                                const isCorrectOpt = opt === correctOption;
                                                let bg = 'transparent';
                                                if (isSelected && isCorrectOpt) bg = `${theme.palette.success.light}40`;
                                                else if (isSelected && !isCorrectOpt) bg = `${theme.palette.error.light}40`;
                                                else if (isCorrectOpt) bg = `${theme.palette.success.light}20`;
                                                return (
                                                    <Grid item xs={12} sm={6} key={idx}>
                                                        <Paper variant="outlined" sx={{
                                                            p: 1.5, borderRadius: 1,
                                                            display: 'flex', alignItems: 'center',
                                                            backgroundColor: bg,
                                                            borderColor: isSelected || isCorrectOpt
                                                                ? (isCorrectOpt
                                                                    ? theme.palette.success.main
                                                                    : theme.palette.error.main)
                                                                : 'divider'
                                                        }}>
                                                            {(isSelected || isCorrectOpt) && (
                                                                <Box sx={{ mr: 1 }}>
                                                                    {isCorrectOpt
                                                                        ? <CheckCircleOutlineIcon color="success" sx={{ fontSize: 18 }} />
                                                                        : <CancelOutlinedIcon color="error" sx={{ fontSize: 18 }} />
                                                                    }
                                                                </Box>
                                                            )}
                                                            <Typography variant="body2" sx={{
                                                                flex: 1,
                                                                fontWeight: isSelected || isCorrectOpt ? 500 : 400
                                                            }}>
                                                                {opt}
                                                            </Typography>
                                                            {isCorrectOpt && (
                                                                <Chip
                                                                    label={t.correctAnswer}
                                                                    size="small"
                                                                    color="success"
                                                                    sx={{ height: 20, ml: 1, '& .MuiChip-label': { px: 1, fontSize: '0.65rem' } }}
                                                                />
                                                            )}
                                                            {isSelected && !isCorrectOpt && (
                                                                <Chip
                                                                    label={t.yourAnswer}
                                                                    size="small"
                                                                    color="error"
                                                                    sx={{ height: 20, ml: 1, '& .MuiChip-label': { px: 1, fontSize: '0.65rem' } }}
                                                                />
                                                            )}
                                                        </Paper>
                                                    </Grid>
                                                );
                                            })}
                                        </Grid>
                                    </Box>
                                </Paper>
                            </Grid>
                        );
                    })}
                </Grid>
            </Fade>

            <Box sx={{ mt: 3, display: 'flex', justifyContent: 'center' }}>
                <Button
                    variant="contained"
                    startIcon={<ArrowBackIcon />}
                    onClick={() => navigate(-1)}
                    sx={{ textTransform: 'none', fontWeight: 500, px: 3 }}
                >
                    {t.returnToQuizList}
                </Button>
            </Box>
        </Container>
    );
}
